
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface AboutUsContent {
  content: string;
  lastUpdated: string;
}

export const useAboutUs = () => {
  const { toast } = useToast();
  const [aboutUs, setAboutUs] = useState<AboutUsContent>({
    content: "<h1>About Us</h1><p>Welcome to our service directory. We connect customers with trusted local service providers.</p>",
    lastUpdated: new Date().toISOString()
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch about us content
  useEffect(() => {
    fetchAboutUsContent();
  }, []);

  const fetchAboutUsContent = async () => {
    setIsLoading(true);
    try {
      // Fetch about_us data directly from the admin_settings table
      const { data, error } = await supabase
        .from('admin_settings')
        .select('about_us')
        .eq('id', 1)
        .single();
        
      if (error) {
        console.error("Error fetching about us content:", error);
        throw error;
      }
      
      if (data && data.about_us) {
        console.log("Fetched about_us data:", data.about_us);
        setAboutUs(data.about_us as AboutUsContent);
      } else {
        console.log("No about_us data found, using default");
      }
    } catch (error) {
      console.error("Error in fetchAboutUsContent:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveAboutUsContent = async (content: string) => {
    setIsSaving(true);
    try {
      // Create updated content object with new timestamp
      const updatedContent: AboutUsContent = {
        content,
        lastUpdated: new Date().toISOString()
      };
      
      console.log("Saving about_us content:", updatedContent);
      
      // Update only the about_us column in admin_settings table
      const { error } = await supabase
        .from('admin_settings')
        .update({ 
          about_us: updatedContent,
          updated_at: new Date().toISOString()
        })
        .eq('id', 1);

      if (error) {
        console.error("Error saving about us content:", error);
        throw error;
      }

      // Update local state
      setAboutUs(updatedContent);
      
      toast({
        title: "About Us saved",
        description: "Your About Us page content has been updated successfully."
      });
      
      return true;
    } catch (error: any) {
      console.error("Error in saveAboutUsContent:", error);
      
      toast({
        variant: "destructive",
        title: "Error saving About Us content",
        description: error.message || "Failed to save About Us content"
      });
      
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return { 
    aboutUs, 
    isLoading, 
    isSaving, 
    fetchAboutUsContent, 
    saveAboutUsContent 
  };
};
